import api from './api';

const roomService = {

  // Overall stats for both towers
  getStats: async () => {
    const r = await api.get('/rooms/stats');
    return r.data;
  },


  // Read-only availability for students (bed status only, no names)
  getAvailability: async () => {
    const r = await api.get('/rooms/availability');
    return r.data; // { stats, towers: [towerA, towerB] }
  },

  // Full tower data: all floors, rooms, beds
  getTower: async (tower) => {
    const r = await api.get(`/rooms/tower/${tower}`);
    return r.data;
  },

  // Single floor data
  getFloor: async (tower, floor) => {
    const r = await api.get(`/rooms/tower/${tower}/floor/${floor}`);
    return r.data;
  },

  // Single room with beds
  getRoom: async (roomCode) => {
    const r = await api.get(`/rooms/${roomCode}`);
    return r.data;
  },

  // All vacant beds (optionally filtered by tower: "A" or "B")
  getVacantBeds: async (tower = null) => {
    const params = tower ? { tower } : {};
    const r = await api.get('/rooms/beds/vacant', { params });
    return r.data;
  },

  // Get bed assigned to a specific admission
  getBedByAdmission: async (admissionId) => {
    const r = await api.get(`/rooms/beds/by-admission/${admissionId}`);
    return r.data;
  },

  // Allocate a bed to a student
  // bedCode format: "AR-111", "AF-231", "BR-211", "BF-252"
  allocateBed: async (admissionId, bedCode, studentName, studentRegId, isPrivate = false) => {
    // ── Guard: check if this student already has a bed allocated ──────────────
    try {
      const existing = await api.get(`/admissions/${admissionId}`);
      const adm = existing.data;
      const alreadyHasBed =
        (adm.bedCode   && adm.bedCode.trim()   !== '') ||
        (adm.roomBlock && adm.roomBlock.trim() !== '');
      if (alreadyHasBed) {
        const current = adm.bedCode || `${adm.roomBlock}-${adm.roomNumber}${adm.bedNumber}`;
        throw new Error(
          `${studentName} already has bed ${current} allocated. ` +
          `Please vacate the current bed before allocating a new one.`
        );
      }
    } catch (e) {
      // Re-throw our own guard errors; ignore network errors on the pre-check
      if (e.message && e.message.includes('already has bed')) throw e;
      console.warn('Pre-allocation check failed (non-fatal):', e.message);
    }
    // ─────────────────────────────────────────────────────────────────────────

    // Step 1: Mark bed as OCCUPIED in rooms/beds table
    const r = await api.post('/rooms/beds/allocate', {
      admissionId, bedCode, studentName, studentRegId,
      isPrivate: isPrivate || false,  // ✅ pass private flag to backend
    });

    // Step 2: Save bedCode back to the admission record
    //         This is critical — without this, getEligibleStudents()
    //         keeps showing this student (bedCode stays null in admissions table)
    //
    //         bedCode "AR-211" → roomBlock="AR", roomNumber="21", bedNumber="1"
    //         bedCode "BF-252" → roomBlock="BF", roomNumber="25", bedNumber="2"
    const dashIdx = bedCode.indexOf('-');
    const prefix  = bedCode.substring(0, dashIdx);       // e.g. "AR"
    const rest    = bedCode.substring(dashIdx + 1);      // e.g. "211"
    const roomNum = rest.substring(0, rest.length - 1);  // e.g. "21"
    const bedNum  = rest.substring(rest.length - 1);     // e.g. "1"

    await api.patch(`/admissions/${admissionId}/room`, {
      roomBlock:     prefix,
      roomNumber:    roomNum,
      bedNumber:     bedNum,
      bedCode:       bedCode,
      wardenName:    '',
      wardenContact: '',
    });

    return r.data;
  },

  // Vacate a bed — also clears the matching admission record
  // so AdmissionScreen shows "Allocate" again instead of old room details
  vacateBed: async (bedCode) => {
    // Step 1: Mark bed as VACANT in beds table
    const r = await api.patch(`/rooms/beds/${encodeURIComponent(bedCode)}/vacate`);

    // Step 2: Find the admission that has this bedCode and clear its room fields
    // We search admissions (up to 200) to find the one with bedCode match
    try {
      const admissionsResp = await api.get('/admissions', { params: { page: 0, size: 200 } });
      const admissions = admissionsResp.data?.content || admissionsResp.data || [];
      const matched = admissions.find(
        (a) => a.bedCode === bedCode || a.roomBlock && a.roomNumber &&
          `${a.roomBlock}-${a.roomNumber}${a.bedNumber}` === bedCode
      );
      if (matched) {
        await api.patch(`/admissions/${matched.id}/room`, {
          roomBlock:     '',
          roomNumber:    '',
          bedNumber:     '',
          bedCode:       '',
          wardenName:    '',
          wardenContact: '',
        });
      }
    } catch (e) {
      // Non-fatal: bed is already vacated, admission clear is best-effort
      console.warn('Could not clear admission room data after vacating bed:', e.message);
    }

    return r.data;
  },

  // ── Room Rate Settings ────────────────────────────────────────────────────
  getRates: async () => {
    const r = await api.get('/room-rates');
    return r.data;
  },

  saveRates: async (payload) => {
    const r = await api.post('/room-rates', payload);
    return r.data;
  },
};

export default roomService;